import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';
import MarkdownIt from 'markdown-it';

const setImageHeight = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  let url;
  try {
    url = new URL(imgSrc);
  } catch {
    // Relative/fragment srcs (e.g. "#wamention-…" from message-content linkify)
    // can't carry cw_image_height; throwing here blanks the whole message bubble.
    return;
  }
  const height = url.searchParams.get('cw_image_height');
  if (!height) return;
  inlineToken.attrSet('style', `height: ${height};`);
};

const processInlineToken = blockToken => {
  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') {
      setImageHeight(inlineToken);
    }
  });
};

const imgResizeManager = md => {
  // Custom rule for image resize in markdown
  // If the image url has a query param cw_image_height, then add a style attribute to the image
  md.core.ruler.after('inline', 'add-image-height', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') {
        processInlineToken(blockToken);
      }
    });
  });
};

const createMarkdownInstance = (linkify = true, waMode = false) => {
  const md = MarkdownIt({
    html: false,
    xhtmlOut: true,
    breaks: true,
    langPrefix: 'language-',
    linkify,
    typographer: true,
    quotes: '\u201c\u201d\u2018\u2019',
    maxNesting: 20,
  })
    .use(mentionPlugin)
    .use(imgResizeManager)
    .use(mila, {
      attrs: {
        class: 'link',
        rel: 'noreferrer noopener nofollow',
        target: '_blank',
      },
    });

  if (waMode) {
    // WhatsApp formatting: a single *bold* is BOLD and _italic_ is italic. Standard
    // markdown (which markdown-it implements) treats single * as emphasis, so a
    // WhatsApp *bold* would otherwise render as <em>. markdown-it tags every emphasis
    // token with the delimiter it used (token.markup), letting us route * -> <strong>
    // and keep _ -> <em>. Double **bold** stays <strong> (its own token).
    md.renderer.rules.em_open = (tokens, idx) =>
      tokens[idx].markup === '*' ? '<strong>' : '<em>';
    md.renderer.rules.em_close = (tokens, idx) =>
      tokens[idx].markup === '*' ? '</strong>' : '</em>';
  }

  return md;
};

// Shopee sticker shortcode → inline image
const SHOPEE_STICKER_REGEX = /:shopee_sticker:(\w+):(\w+):/g;
const SHOPEE_STICKER_CDN =
  'https://deo.shopeemobile.com/shopee/shopee-sticker-live-my/packs';
const replaceShopeeStickers = msg =>
  msg.replace(
    SHOPEE_STICKER_REGEX,
    (_, pkg, id) =>
      `<img src="${SHOPEE_STICKER_CDN}/${pkg}/${id}@1x.png" alt="sticker" style="width:96px;height:96px;display:block;" />`
  );

// [Product:SHOPEE_ID:SKU] tag → hidden placeholder (rendered by Text bubble via contentAttributes.products)
const PRODUCT_TAG_REGEX = /\[Product:[^\]]*\]/g;
const replaceProductTags = msg => msg.replace(PRODUCT_TAG_REGEX, '').trim();

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';

class MessageFormatter {
  constructor(
    message,
    isATweet = false,
    isAPrivateNote = false,
    linkify = true,
    waMode = false
  ) {
    this.message = message || '';
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.linkify = linkify;
    this.waMode = waMode;
    this.md = createMarkdownInstance(linkify, waMode);
  }

  formatMessage() {
    let updatedMessage = this.message;
    if (this.isATweet && !this.isAPrivateNote) {
      updatedMessage = updatedMessage.replace(
        TWITTER_USERNAME_REGEX,
        TWITTER_USERNAME_REPLACEMENT
      );
      updatedMessage = updatedMessage.replace(
        TWITTER_HASH_REGEX,
        TWITTER_HASH_REPLACEMENT
      );
    }
    const rendered = this.md.render(updatedMessage);
    return replaceProductTags(replaceShopeeStickers(rendered));
  }

  get formattedMessage() {
    return this.formatMessage();
  }

  get plainText() {
    const strippedOutHtml = new DOMParser().parseFromString(
      this.formattedMessage,
      'text/html'
    );
    return strippedOutHtml.body.textContent || '';
  }
}

export default MessageFormatter;
